'use client'

import { useState, useEffect } from 'react'
import { ArrowLeft, FileText, User, Mail, Phone } from 'lucide-react'
import UrlPreview from './UrlPreview'
import { panel, panelInner, headingClass, mutedClass, ghostBtn, primaryBtn, primaryBtnInline, fieldClass, labelClass, errorBox, successBox } from './ui'

interface BriefViewProps {
  briefId: string
  userId: string
  onBack: () => void
}

export default function BriefView({ briefId, userId, onBack }: BriefViewProps) {
  const [brief, setBrief] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [isManager, setIsManager] = useState(false)

  useEffect(() => {
    loadBrief()
  }, [briefId, userId])

  const loadBrief = async () => {
    try {
      // Check if this is a manager (stored in localStorage)
      const managerId = localStorage.getItem('brief613_managerId')
      const isManagerView = !!managerId
      setIsManager(isManagerView)
      
      const headers: any = {
        'x-user-id': userId,
        'x-user-role': isManagerView ? 'manager' : 'client',
      }
      if (managerId) {
        headers['x-manager-id'] = managerId
      }

      const response = await fetch(`/api/brief613/briefs/${briefId}`, {
        headers,
      })

      const data = await response.json()

      if (data.success) {
        setBrief(data.brief)
      }
    } catch (error) {
      console.error('Error loading brief:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className={`${panel} p-8 max-w-4xl mx-auto text-center`}>
        <div className="w-12 h-12 border-4 border-white/20 border-t-white/80 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/55">Loading brief...</p>
      </div>
    )
  }

  if (!brief) {
    return (
      <div className={`${panel} p-8 max-w-4xl mx-auto text-center`}>
        <p className="text-white/55">Brief not found</p>
        <button onClick={onBack} className={ghostBtn}>
          Back
        </button>
      </div>
    )
  }

  const deliverables = brief.deliverables ? (typeof brief.deliverables === 'string' ? JSON.parse(brief.deliverables) : brief.deliverables) : []

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onBack} className={ghostBtn}>
          <ArrowLeft size={18} />
          Back
        </button>
        <span
          className={`px-3 py-1 text-xs font-semibold rounded-full border ${
            brief.status === 'submitted'
              ? 'bg-blue-500/10 text-blue-300/90 border-blue-500/20'
              : brief.status === 'in_progress'
              ? 'bg-yellow-500/10 text-yellow-300/90 border-yellow-500/20'
              : brief.status === 'completed'
              ? 'bg-emerald-500/10 text-emerald-300/90 border-emerald-500/20'
              : 'bg-white/5 text-white/50 border-white/10'
          }`}
        >
          {brief.status}
        </span>
      </div>

      <div className={`${panel} p-8`}>
        <div className="flex items-center gap-3 mb-6">
          <FileText className="text-white/50" size={22} />
          <div>
            <h2 className="text-base font-semibold text-white/90 sm:text-lg">Creative Brief</h2>
            {brief.reference && (
              <p className="text-white/90 font-medium mb-1">
                Reference: <span className="text-white/60">{brief.reference}</span>
              </p>
            )}
            <p className="text-white/70">
              {brief.companyProfile?.companyName || 'No company'} • Created{' '}
              {new Date(brief.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Brief Type */}
          <div>
            <h3 className="text-sm font-medium text-white/60 mb-2">Brief Type</h3>
            <p className="text-white/90 capitalize">{brief.briefType ? brief.briefType.replace('_', ' ') : 'N/A'}</p>
          </div>

          {/* Campaign Title */}
          <div>
            <h3 className="text-sm font-medium text-white/60 mb-2">Campaign Title</h3>
            <p className="text-white/90">{brief.campaignTitle || 'N/A'}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-white/60 mb-2">Objective</h3>
            <p className="text-white/90">{brief.objective || 'N/A'}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-white/60 mb-2">Target Audience</h3>
            <p className="text-white/90 whitespace-pre-wrap">{brief.targetAudience || 'N/A'}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-white/60 mb-2">Desired Reaction</h3>
            <p className="text-white/90">{brief.desiredReaction || 'N/A'}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-white/60 mb-2">Key Message</h3>
            <p className="text-white/90 whitespace-pre-wrap">{brief.keyMessage || 'N/A'}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-white/60 mb-2">Tone or Style</h3>
            <p className="text-white/90">{brief.tone || 'N/A'}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-white/60 mb-2">Deliverables & Channels</h3>
            {deliverables.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-2">
                {deliverables.map((deliverable: string, index: number) => (
                  <span
                    key={index}
                    className="px-3 py-1 text-sm rounded-full bg-white/10 border border-white/20 text-white/55"
                  >
                    {deliverable}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-white/90">N/A</p>
            )}
          </div>

          {/* Inspiration URLs */}
          <div>
            <h3 className="text-sm font-medium text-white/60 mb-2">Inspiration URLs</h3>
            {brief.inspirationUrls ? (() => {
              try {
                const urls = typeof brief.inspirationUrls === 'string' 
                  ? JSON.parse(brief.inspirationUrls) 
                  : brief.inspirationUrls
                if (Array.isArray(urls) && urls.length > 0) {
                  return (
                    <div className="space-y-3">
                      {urls.map((url: string, index: number) => (
                        <UrlPreview key={index} url={url} />
                      ))}
                    </div>
                  )
                }
              } catch (e) {
                // Invalid JSON, show as text
                return <p className="text-white/90 whitespace-pre-wrap">{brief.inspirationUrls}</p>
              }
              return <p className="text-white/90">N/A</p>
            })() : <p className="text-white/90">N/A</p>}
          </div>

          {/* Brand Assets */}
          <div>
            <h3 className="text-sm font-medium text-white/60 mb-2">Brand Assets & Guidelines</h3>
            <p className="text-white/90 whitespace-pre-wrap">{brief.brandAssets || 'N/A'}</p>
          </div>

          {/* Constraints */}
          <div>
            <h3 className="text-sm font-medium text-white/60 mb-2">Constraints & Limitations</h3>
            <p className="text-white/90 whitespace-pre-wrap">{brief.constraints || 'N/A'}</p>
          </div>

          {/* Budget */}
          <div>
            <h3 className="text-sm font-medium text-white/60 mb-2">Budget</h3>
            <p className="text-white/90">{brief.budget || 'N/A'}</p>
          </div>

          {/* Event Information */}
          {(brief.briefType === 'event' || brief.briefType === 'custom' || brief.numberOfParticipants || brief.eventLocations) && (
            <div className="border-t border-white/10 pt-6">
              <h3 className="text-lg font-semibold text-white/90 mb-4">Event Information</h3>
              
              <div className="mb-4">
                <h4 className="text-sm font-medium text-white/60 mb-2">Total Number of Participants</h4>
                <p className="text-white/90">{brief.numberOfParticipants || 'N/A'}</p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-white/60 mb-3">Event Locations</h4>
                {brief.eventLocations ? (() => {
                  try {
                    const locations = typeof brief.eventLocations === 'string' 
                      ? JSON.parse(brief.eventLocations) 
                      : brief.eventLocations
                    if (Array.isArray(locations) && locations.length > 0) {
                      return (
                        <div className="space-y-4">
                          {locations.map((location: any, index: number) => (
                            <div key={index} className={`${panelInner} p-4`}>
                              <h5 className="text-sm font-semibold text-white/55 mb-3">Location {index + 1}</h5>
                              <div className="space-y-2 text-sm">
                                <p><span className="text-white/60">Country:</span> <span className="text-white/90">{location.country || 'N/A'}</span></p>
                                <p><span className="text-white/60">Address:</span> <span className="text-white/90">{location.address || 'N/A'}</span></p>
                                <p><span className="text-white/60">Start Date:</span> <span className="text-white/90">{(location.dateStart || location.date_start) ? new Date(location.dateStart || location.date_start).toLocaleDateString() : 'N/A'}</span></p>
                                <p><span className="text-white/60">End Date:</span> <span className="text-white/90">{(location.dateEnd || location.date_end) ? new Date(location.dateEnd || location.date_end).toLocaleDateString() : 'N/A'}</span></p>
                                <p><span className="text-white/60">Start Time:</span> <span className="text-white/90">{location.timeStart || location.time_start || 'N/A'}</span></p>
                                <p><span className="text-white/60">End Time:</span> <span className="text-white/90">{location.timeEnd || location.time_end || 'N/A'}</span></p>
                                <p><span className="text-white/60">Participants:</span> <span className="text-white/90">{location.participants || 'N/A'}</span></p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    }
                  } catch (e) {
                    // Invalid JSON, show as text
                    return <p className="text-white/90 whitespace-pre-wrap">{brief.eventLocations}</p>
                  }
                  return <p className="text-white/90">N/A</p>
                })() : <p className="text-white/90">N/A</p>}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-white/60 mb-2">Deadline</h3>
              <p className="text-white/90">
                {brief.deadline ? new Date(brief.deadline).toLocaleDateString() : 'N/A'}
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-white/60 mb-2">Approval Contact</h3>
              <p className="text-white/90">
                {brief.approvalContactName || 'N/A'}
                {brief.approvalContactEmail && (
                  <>
                    <br />
                    <a href={`mailto:${brief.approvalContactEmail}`} className="text-blue-400 hover:underline">
                      {brief.approvalContactEmail}
                    </a>
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Additional Notes */}
          <div>
            <h3 className="text-sm font-medium text-white/60 mb-2">Additional Notes</h3>
            <p className="text-white/90 whitespace-pre-wrap">{brief.additionalNotes || 'N/A'}</p>
          </div>

          {/* Admin Notes - Only visible to managers, not clients */}
          {isManager && brief.adminNotes && (
            <div className="border-t border-white/10 pt-6">
              <h3 className="text-sm font-medium text-white/60 mb-2">Admin Notes</h3>
              <div className={`${panelInner} p-4 bg-white/5`}>
                <p className="text-white/90 whitespace-pre-wrap">{brief.adminNotes}</p>
              </div>
            </div>
          )}

          {/* Assigned Manager Section */}
          {brief.assignedManager && (
            <div className="mt-6 p-6 rounded-lg border bg-white/5 border-white/10">
              <h3 className="text-sm font-semibold text-white/90 mb-4 flex items-center gap-2">
                <User size={20} className="text-white/50" />
                Project Manager
              </h3>
              <div className="flex items-start gap-4">
                {brief.assignedManager.photoUrl ? (
                  <img
                    src={brief.assignedManager.photoUrl}
                    alt={brief.assignedManager.name}
                    className="w-16 h-16 rounded-full object-cover border border-white/10"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-white/[0.03] flex items-center justify-center border border-white/10">
                    <User size={32} className="text-white/50" />
                  </div>
                )}
                <div className="flex-1">
                  <h4 className="text-xl font-semibold text-white/90 mb-2">{brief.assignedManager.name}</h4>
                  <div className="space-y-2">
                    {brief.assignedManager.email && (
                      <div className="flex items-center gap-2 text-white/55">
                        <Mail size={16} className="text-white/50" />
                        <a href={`mailto:${brief.assignedManager.email}`} className="text-blue-400 hover:underline">
                          {brief.assignedManager.email}
                        </a>
                      </div>
                    )}
                    {brief.assignedManager.phone && (
                      <div className="flex items-center gap-2 text-white/55">
                        <Phone size={16} className="text-white/50" />
                        <a href={`tel:${brief.assignedManager.phone}`} className="text-blue-400 hover:underline">
                          {brief.assignedManager.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

